import { useMutation } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { apiV1 } from "api-v2";

import type { AuthenticateResponse } from "../types/AuthenticateResponse.type";

// `expectedErrors` is a custom escape hatch read by the api-v2 response
// interceptor (src/api-v2.js) to silence its default error-toast for listed
// statuses — not a real axios config field, so it's typed separately here to
// avoid the excess-property check on an inline literal.
interface ApiV2RequestConfig extends AxiosRequestConfig {
	expectedErrors?: number[];
}

export interface TwoFactorChallengePayload {
	mfa_token: string;
	code?: string;
	recovery_code?: string;
	remember_device?: boolean;
}

/**
 * React Query mutation for `POST auth/two-factor-challenge` — verifies the
 * pending login's second factor (TOTP `code` OR single-use `recovery_code`) and,
 * on success, establishes the session (cookie) and returns the full
 * authenticated user. The `mfa_token` is held in component state only and never
 * persisted. `retry: false` so a wrong code / lockout is surfaced immediately
 * rather than silently re-submitted.
 */
export const useTwoFactorChallenge = () =>
	useMutation<AuthenticateResponse, unknown, TwoFactorChallengePayload>({
		mutationFn: async (payload) => {
			// The challenge component owns the user-facing signal for a wrong code
			// (422) and lockout (429) via its inline FlashError / expired handling.
			// Silence those in the global interceptor so it does not ALSO fire a
			// toast — that toast lingered on screen into the next, successful
			// attempt and read as "invalid code on successful login".
			const config: ApiV2RequestConfig = { expectedErrors: [422, 429] };
			const res = await apiV1.post<AuthenticateResponse>(
				"auth/two-factor-challenge",
				payload,
				config
			);
			return res.data;
		},
		retry: false,
	});
